import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { myToaster } from '@interstellar-component'
import { useApp } from '@src/AppContext'
import { CompanyService } from './service'

const CompanyContext = createContext()

/** Gabungkan field dari `company_information` / `compliance_documents` ke root untuk header & subtitle. */
function enrichCompanyDetailPayload(data) {
  if (!data || typeof data !== 'object') return data
  const ci = data.company_information
  const out = { ...data }
  if (ci && typeof ci === 'object') {
    out.name = out.name ?? ci.name
    out.legal_name = out.legal_name ?? ci.legal_name
    out.email = out.email ?? ci.email
  }
  const cd = data.compliance_documents
  if (cd && typeof cd === 'object') {
    out.nib = out.nib ?? cd.nib
    out.npwp = out.npwp ?? cd.npwp
  }
  return out
}

/**
 * Gabungkan response detail per `type` agar header (nama, NIB, enrollment, dll.) tidak hilang
 * saat pindah tab — response result/billing sering tidak menyertakan `company_information`.
 */
function mergeCompanyDetail(prev, incoming, id) {
  const next = enrichCompanyDetailPayload(incoming)
  if (!next || typeof next !== 'object') return prev
  if (!prev || typeof prev !== 'object' || String(prev.id) !== String(id)) {
    return next
  }

  const ci = next.company_information ?? prev.company_information
  const cd = next.compliance_documents ?? prev.compliance_documents
  const gi =
    next.general_information !== undefined
      ? next.general_information
      : prev.general_information

  return {
    ...prev,
    ...next,
    company_information: ci,
    compliance_documents: cd,
    general_information: gi,
    activity: next.activity !== undefined ? next.activity : prev.activity,
    name: next.name ?? prev.name ?? ci?.name,
    legal_name: next.legal_name ?? prev.legal_name ?? ci?.legal_name,
    company_id: next.company_id ?? prev.company_id,
    enrollment_status: next.enrollment_status ?? prev.enrollment_status,
    enrollment_step: next.enrollment_step ?? prev.enrollment_step,
    // Carry membership fields so the header status (memberStatusOf) stays
    // correct across tabs — only the billing payload (full company) includes
    // them, so without this the header flips e.g. "Completed" → "Waiting
    // approval" when switching to Billing.
    verification_status: next.verification_status ?? prev.verification_status,
    // The rejection verdict ({ note, reviewed_at }) only rides the "result"
    // payload, so carry it too — otherwise the rejection panel's reason
    // vanishes when the user switches tabs and back.
    verification: next.verification ?? prev.verification,
    agreement_expires_at: next.agreement_expires_at ?? prev.agreement_expires_at,
    nib: next.nib ?? prev.nib ?? cd?.nib,
    npwp: next.npwp ?? prev.npwp ?? cd?.npwp,
    email: next.email ?? prev.email ?? ci?.email,
  }
}

/** Subtitle di slider: company_id berformat (konsisten di semua tab) / id. */
export function formatCompanyDetailSubtitle(detail) {
  if (!detail || typeof detail !== 'object') return ''
  // Keep the subtitle stable across tabs. NIB / compliance_documents only
  // arrive in the "information" payload, so falling back to it caused the
  // header ID to flip when the user switched tabs. company_id is included
  // in every detail-type response, which keeps Result/Billing/Information
  // aligned.
  const cid = detail.company_id
  if (cid != null && String(cid).trim() !== '') {
    const s = String(cid).trim()
    if (/^id[-\s]/i.test(s)) return s
    if (/^\d{10,}$/.test(s)) return s
    const rest = s.replace(/^id-?/i, '').trim()
    return rest ? `ID-${rest}` : ''
  }
  if (detail.id != null && String(detail.id).trim() !== '') return String(detail.id)
  return ''
}

/** Keep API row shape; only merge `checked` for table selection. */
function withSelectionRow(item) {
  if (!item || typeof item !== 'object') return null
  return { ...item, checked: Boolean(item.checked) }
}

/** MyColumn `field` → query `sort` param (backend snake_case). */
const SORT_FIELD_TO_API = {
  name: 'name',
  enrollment_status: 'enrollment_status',
  enrollment_step: 'enrollment_step',
  // Synthetic FE column names → real DB columns rendered in that column.
  // "Member status" chip is driven by enrollment_status; "Quota left" cell
  // currently shows enrollment_step (no dedicated quota column exists yet).
  member_status: 'enrollment_status',
  quota_left: 'enrollment_step',
}

function CompanyProvider({ children }) {
  const { setSlider } = useApp()

  const [currentSlider, setCurrentSlider] = useState({
    status: false,
    current: null,
    id: null,
  })

  // Side-panel stacking for billing flow. Mirrors the ReportEnquiry slider
  // stack pattern: secondary panels (topup quota, subscribe to plan) open
  // beside the main DetailSlider via flex-row-reverse in the MyModalSlider
  // element prop. Only one secondary panel open at a time.
  const [topupPanelOpen, setTopupPanelOpen] = useState(false)
  const [subscribePanelOpen, setSubscribePanelOpen] = useState(false)
  // "Reject & resend" form — opened from the rejected-application panel in the
  // detail slider's Result tab. Same stack as the billing panels (one at a time).
  const [resendPanelOpen, setResendPanelOpen] = useState(false)
  // Invoice payment detail panel — opened by clicking a row in the
  // "Billing and invoicing" table. Carries the clicked invoice id.
  const [invoicePanelId, setInvoicePanelId] = useState(null)
  // Shared "billing changed" signal. Bumped after any billing action (or when
  // a billing panel closes) so the Billing tab re-fetches without a manual
  // refresh — e.g. a new pending invoice / waiting-payment status shows up.
  const [billingRefreshKey, setBillingRefreshKey] = useState(0)
  const refreshBilling = useCallback(() => setBillingRefreshKey((k) => k + 1), [])
  const openTopupPanel = useCallback(() => {
    setSubscribePanelOpen(false)
    setInvoicePanelId(null)
    setResendPanelOpen(false)
    setTopupPanelOpen(true)
  }, [])
  const openResendPanel = useCallback(() => {
    setTopupPanelOpen(false)
    setSubscribePanelOpen(false)
    setInvoicePanelId(null)
    setResendPanelOpen(true)
  }, [])
  const closeResendPanel = useCallback(() => {
    setResendPanelOpen(false)
  }, [])
  const closeTopupPanel = useCallback(() => {
    setTopupPanelOpen(false)
    setBillingRefreshKey((k) => k + 1)
  }, [])
  const openSubscribePanel = useCallback(() => {
    setTopupPanelOpen(false)
    setInvoicePanelId(null)
    setResendPanelOpen(false)
    setSubscribePanelOpen(true)
  }, [])
  const closeSubscribePanel = useCallback(() => {
    setSubscribePanelOpen(false)
    setBillingRefreshKey((k) => k + 1)
  }, [])
  const openInvoicePanel = useCallback((invoiceId) => {
    setTopupPanelOpen(false)
    setSubscribePanelOpen(false)
    setResendPanelOpen(false)
    setInvoicePanelId(invoiceId ?? null)
    // A new invoice was just created (subscribe / topup / change plan) — refresh
    // the Billing tab now so the new invoice + subscription status (e.g. pending
    // plan change) show immediately alongside the panel, not only after close.
    setBillingRefreshKey((k) => k + 1)
  }, [])
  const closeInvoicePanel = useCallback(() => {
    setInvoicePanelId(null)
    setBillingRefreshKey((k) => k + 1)
  }, [])
  const closeBillingPanels = useCallback(() => {
    setTopupPanelOpen(false)
    setSubscribePanelOpen(false)
    setInvoicePanelId(null)
    setResendPanelOpen(false)
  }, [])

  const [company, setCompany] = useState({
    data: [],
    meta: {},
    filter: [],
    loading: false,
  })

  const [companyDetail, setCompanyDetail] = useState(null)
  const [isLoadingCompanyDetail, setIsLoadingCompanyDetail] = useState(false)

  const [params, setParams] = useState({
    page: 1,
    limit: 10,
    search: '',
    filter: [],
    sort: null,
    order: null,
    status: 'All status',
  })

  useEffect(() => {
    const open = currentSlider.status && currentSlider.current === 'details-slider'
    setSlider(!!open)
  }, [currentSlider, setSlider])

  const fetchCompanyDetail = useCallback(async (id, query = {}) => {
    if (id == null || id === '') return
    setIsLoadingCompanyDetail(true)
    try {
      const res = await CompanyService.getDetailCompany(id, query)
      const d = res?.data !== undefined ? res.data : res
      setCompanyDetail((prev) => mergeCompanyDetail(prev, d, id))
    } catch (e) {
      myToaster(e)
      setCompanyDetail(null)
    } finally {
      setIsLoadingCompanyDetail(false)
    }
  }, [])

  const handleCurrentSlider = useCallback(
    (slider, id, listRow) => {
      if (slider?.current) {
        setCurrentSlider({
          status: true,
          current: slider.current,
          id: id ?? null,
        })
        if (slider.current === 'details-slider' && listRow && typeof listRow === 'object') {
          const resolvedId = id ?? listRow.id
          setCompanyDetail({
            id: resolvedId,
            name: listRow.name,
            legal_name: listRow.legal_name,
            company_id: listRow.company_id,
            enrollment_status: listRow.enrollment_status,
            enrollment_step: listRow.enrollment_step,
            // Seed membership fields from the list row so the header status is
            // correct on first paint (the Result payload omits them).
            verification_status: listRow.verification_status,
            agreement_expires_at: listRow.agreement_expires_at,
            // Agreement chip next to the member chip on first paint; every
            // detail type re-sends these (plus the full `agreement` block).
            agreement_status: listRow.agreement_status,
            client_signed_at: listRow.client_signed_at,
            clik_signed_at: listRow.clik_signed_at,
          })
        }
      } else {
        setCurrentSlider((v) => ({ ...v, current: null }))
        setTimeout(() => {
          setCurrentSlider({ status: false, current: null, id: null })
          setCompanyDetail(null)
        }, 200)
      }
    },
    []
  )

  const getCompany = useCallback(async () => {
    const limit = params.limit ?? 10
    const sortApi = params.sort ? SORT_FIELD_TO_API[params.sort] ?? params.sort : null
    const query = {
      page: params.page,
      limit,
      ...(params.search ? { search: params.search } : {}),
      ...(Array.isArray(params.filter) && params.filter.length > 0 ? { filter: params.filter } : {}),
      ...(sortApi ? { sort: sortApi } : {}),
      ...(params.order ? { order: params.order } : {}),
      ...(params.status && params.status !== 'All status' ? { member_status: params.status } : {}),
    }
    setCompany((s) => ({ ...s, loading: true }))
    try {
      const res = await CompanyService.getCompany(query)
      const rawRows = Array.isArray(res.data) ? res.data : []
      const rows = rawRows.map((r) => withSelectionRow(r)).filter(Boolean)
      // Status filtering is now server-side (member_status param) — trust the
      // server's meta/total for pagination.
      const rawMeta = res.meta
      const perPageForMeta = Number(rawMeta?.per_page ?? rawMeta?.limit ?? limit) || limit
      const meta =
        rawMeta && typeof rawMeta === 'object' && !Array.isArray(rawMeta)
          ? {
              ...rawMeta,
              total_page:
                rawMeta.total_page ??
                rawMeta.last_page ??
                Math.max(1, Math.ceil(Number(rawMeta.total ?? rows.length) / perPageForMeta)),
            }
          : {
              current_page: 1,
              per_page: limit,
              total: rows.length,
              total_page: Math.max(1, Math.ceil(rows.length / perPageForMeta)),
            }
      setCompany({
        data: rows,
        meta,
        filter: res.filter ?? [],
        loading: false,
      })
    } catch (e) {
      myToaster(e)
      setCompany((s) => ({ ...s, loading: false }))
    }
  }, [params])

  useEffect(() => {
    getCompany()
  }, [getCompany])

  const handleCompanySort = useCallback(({ sort, order }) => {
    setParams((p) => ({
      ...p,
      page: 1,
      sort: sort ?? null,
      order: order ?? null,
    }))
  }, [])

  const handleCompanySelectionChange = useCallback((updated) => {
    setCompany((prev) => ({
      ...prev,
      data: updated.data,
    }))
  }, [])

  const setPage = useCallback((page) => {
    setParams((p) => ({ ...p, page }))
  }, [])

  const setSearchTerm = useCallback((search) => {
    setParams((p) => ({ ...p, page: 1, search }))
  }, [])

  const setSelectedStatus = useCallback((status) => {
    setParams((p) => ({ ...p, page: 1, status }))
  }, [])

  const companyRows = useMemo(
    () => (Array.isArray(company?.data) ? company.data : []),
    [company?.data]
  )

  const companyMeta = useMemo(() => {
    const m = company?.meta
    if (m && typeof m === 'object' && !Array.isArray(m)) return m
    return {
      current_page: 1,
      per_page: params.limit ?? 10,
      total: companyRows.length,
      total_page: 1,
    }
  }, [company?.meta, companyRows.length, params.limit])

  const contextValue = useMemo(
    () => ({
      params,
      setParams,
      company,
      companyRows,
      companyMeta,
      getCompany,
      handleCompanySort,
      handleCompanySelectionChange,
      companyDetail,
      isLoadingCompanyDetail,
      fetchCompanyDetail,
      currentSlider,
      handleCurrentSlider,
      topupPanelOpen,
      openTopupPanel,
      closeTopupPanel,
      subscribePanelOpen,
      openSubscribePanel,
      closeSubscribePanel,
      resendPanelOpen,
      openResendPanel,
      closeResendPanel,
      invoicePanelId,
      openInvoicePanel,
      closeInvoicePanel,
      closeBillingPanels,
      billingRefreshKey,
      refreshBilling,
      setPage,
      searchTerm: params.search,
      setSearchTerm,
      selectedStatus: params.status,
      setSelectedStatus,
      sortField: params.sort,
      sortOrder: params.order,
    }),
    [
      params,
      company,
      companyRows,
      companyMeta,
      getCompany,
      handleCompanySort,
      handleCompanySelectionChange,
      companyDetail,
      isLoadingCompanyDetail,
      fetchCompanyDetail,
      currentSlider,
      handleCurrentSlider,
      topupPanelOpen,
      openTopupPanel,
      closeTopupPanel,
      subscribePanelOpen,
      openSubscribePanel,
      closeSubscribePanel,
      resendPanelOpen,
      openResendPanel,
      closeResendPanel,
      invoicePanelId,
      openInvoicePanel,
      closeInvoicePanel,
      closeBillingPanels,
      billingRefreshKey,
      refreshBilling,
      setPage,
      setSearchTerm,
      setSelectedStatus,
    ]
  )

  return <CompanyContext.Provider value={contextValue}>{children}</CompanyContext.Provider>
}

const useCompany = () => {
  const context = useContext(CompanyContext)
  if (context === undefined) {
    throw new Error('useCompany must be used within a CompanyProvider')
  }
  return context
}

export { CompanyProvider, useCompany }
